import { DateRange } from './RMChangeLog';

export type SurchargeSuggestionDB = {
  id: string;
  offer_id: string;
  vendor_id: string;
  property_id: string;
  room_type_id: string;
  room_rate_id: string;
  offer_name: string;
  property_name: string;
  room_type_name: string;
  suggested_start_date: string;
  suggested_end_date: string;
  before_price: number;
  suggested_after_price: number;
  suggested_surcharge: number;
  next_cheapest_room_price?: number;
  next_cheapest_room_id?: string;
  score?: number;
  confidence?: string;
  created_at?: string;
  booked?: number;
  allocated?: number;
  actioned_start_date: string;
  actioned_end_date: string;
  actioned_surcharge: number;
  created_by_username?: string;
  created_by_user_id?: string;
  status: 'accepted' | 'rejected';
  note?: string;
  fk_surcharge_date_block_id?: string;
  exp_multiplier?: number;
  exp_actioned_surcharge?: number;
  charter_type?: string;
  booked_sum_minus_two_days?: number;
  allocated_sum_minus_two_days?: number;
  booked_sum_plus_two_days?: number;
  allocated_sum_plus_two_days?: number;
};

export type SuggestionType = 'increase' | 'remove';

export type SurchargeSuggestionBQ = Omit<SurchargeSuggestionDB, 'id'> & {
  days_passed: number;
  days_remaining: number;
  sold_since_last_surcharge: number;
  top_up_since_last_surcharge: number;
  sales_last_3_days: number;
  vendor_surcharge_total: number;
  internal_surcharge_total: number;
  nightly_price_aud: number;
  multiplier: number;
  suggestion_type: SuggestionType;
};

export type LerePaginatedResponse<T> = {
  result: Array<T>;
  timestamp?: number;
  total: number;
};

export type LereTabComponentProps = {
  onLereError: (error: Error | unknown) => void;
};

export type ChangeLogFilters = {
  propertyName?: string;
  roomTypeName?: string;
  actionedDateRange?: DateRange;
  status?: string;
  onlyCharters?: CharterFilteringOptions;
  enforceRoomTypeGranularity: boolean;
};
export type SurchargeSuggestionFilters = {
  aggregate?: boolean;
  offerIdEquals?: string;
  propertyNameLike?: string;
  roomTypeNameLike?: string;
  internalSurchargeEquals?: number;
  salesGte?: number;
  allocatedGte?: number;
  suggestionType?: SuggestionType;
  confidence?: ConfidenceScoreTier;
  sortBy?: string;
  ascending?: boolean;
  onlyCharters?: CharterFilteringOptions;
};

export enum ConfidenceScoreTier {
  ANY = 'any',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum CharterFilteringOptions {
  NONE = 'None',
  ONLY_CHARTERS = 'only_charters',
  ONLY_NON_CHARTERS = 'only_non_charters',
}

export type LereAbandonedCarts = {
  result: Array<LereAbandonedCart>;
};

export interface LereAbandonedCart {
  cartId?: string;
  regionCode?: string;
  currencyCode?: string;
  items?: Array<LereAbandonedCartItem>;
  offer_id?: string;
  offer_type?: string;
  creation_time?: number;
  total?: number;
  suggestionType?: SuggestionType;
}

export interface LereAbandonedCartItem {
  offerId?: string;
  checkIn?: string;
  checkOut?: string;
  duration?: number;
  occupancy?: {
    adults: number;
    children: number;
    childrenAge: Array<number>;
  };
}

// Cluster types
export interface Cluster {
  cluster_id: number;
  centroid_latitude: number;
  centroid_longitude: number;
  offer_count: number;
  polygon_geojson: string;
}

export interface ClustersResponse {
  run_id: string;
  clustering_config_id: string;
  created_at: string;
  clusters: Array<Cluster>;
}

export interface Clustering {
  run_id: string;
  clustering_config_id: string;
  created_at: string;
  algo_version: string;
  params_json: string;
  noise_policy?: string;
}

export interface ManualCluster {
  name: string;
  polygon: Array<LatLongTuple>;
  enabled?: boolean;
  protected?: boolean;
}

export interface ClusteringsResponse {
  clusterings: Array<Clustering>;
  count: number;
}

export interface ClusteringParamsInput {
  cut_km?: number;
  min_cluster_size?: number;
  min_samples?: number;
  max_assign_dist_km?: number;
  manual_clusters?: Array<ManualCluster>;
}

export interface ClusteringRequest {
  params?: ClusteringParamsInput;
  noise_policy?: 'nearest_cluster' | 'leave_noise';
  algo_version?: string;
  data_snapshot_id?: string;
}

export interface ClusteringRunResponse extends ClustersResponse {
  algo_version: string;
  params_json: string;
  noise_policy: string;
  data_snapshot_id?: string | null;
  assignments_count: number;
  noise_count: number;
  noise_pct: number;
}

export type LatLongTuple = [number, number];

export type ClusterOverlay = {
  cluster: Cluster;
  color: string;
  tooltipLabel: string;
  paths: Array<Array<LatLongTuple>>;
};

export type ClusteringRunParams = {
  cut_km?: number;
  min_cluster_size?: number;
  min_samples?: number;
  max_assign_dist_km?: number;
  manual_clusters?: Array<ManualCluster>;
  merge_regions?: Array<ManualCluster>;
  raw_params_json?: string;
  [key: string]: unknown;
};

export type NoiseSummary = {
  noiseCount: number;
  totalPoints: number;
  noisePct: number;
};

export type ManualClusterValidation = {
  hasPolygon: boolean;
  polygonInRange: boolean;
};
